/**
 * BookingPolicySettingsSection — injected into Settings via render-prop
 * (Prompt 11F.8) — Settings must not import from Bookings directly (ESLint
 * feature-boundary rule), same pattern as LeavePolicySettingsSection.
 *
 * Deliberately narrow: only the two flags the booking service actually
 * enforces at write time. The mock Settings "Booking Policies" tab's other
 * fields (online booking toggle, lead time, buffer time, reschedule limit)
 * had no consuming logic anywhere — dropped, logged in the 11A.8 deferred
 * backlog rather than shipped as a fake save.
 */

import React from 'react';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { Card, Toggle } from '@/components/common';
import { useTenant } from '@core/tenant/TenantContext';
import { useBookingsConfig, useUpdateBookingsConfig } from '@features/bookings/api/config';
import type { ApiError } from '@core/api/client';

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing.md} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  &:last-child {
    border-bottom: none;
  }
`;

const ToggleInfo = styled.div``;
const ToggleLabel = styled.div`
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  margin-bottom: 2px;
`;
const ToggleDescription = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const ReadOnlyBanner = styled.div`
  padding: ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  background: ${({ theme }) => theme.colors.backgroundTertiary};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

export const BookingPolicySettingsSection: React.FC = () => {
  const { isAllMode } = useTenant();
  const { data: config, isLoading } = useBookingsConfig();
  const updateConfig = useUpdateBookingsConfig();

  const toggle = async (key: 'trials_require_confirmation' | 'allow_force_override') => {
    const current = config?.flags[key] ?? true;
    try {
      await updateConfig.mutateAsync({ [key]: !current });
      toast.success('Booking policy updated.');
    } catch (err) {
      toast.error((err as ApiError).message || 'Could not update booking policy.');
    }
  };

  return (
    <Card padding="lg" data-testid="candeza-settings-section-booking">
      <h3 style={{ marginTop: 0 }}>Booking Policies</h3>
      <p style={{ fontSize: 14, color: '#64748b', marginTop: 0 }}>
        Cancellation window: {config?.late_cancellation_window_hours ?? '—'} hours (fixed system
        default).
      </p>

      {isAllMode && (
        <ReadOnlyBanner data-testid="candeza-booking-all-mode-banner">
          Select a school to edit Booking Policies.
        </ReadOnlyBanner>
      )}

      <ToggleRow>
        <ToggleInfo>
          <ToggleLabel>Trials require confirmation</ToggleLabel>
          <ToggleDescription>
            When on, a new trial booking starts as pending until an admin confirms it.
          </ToggleDescription>
        </ToggleInfo>
        <Toggle
          active={config?.flags.trials_require_confirmation ?? true}
          onToggle={() => toggle('trials_require_confirmation')}
          disabled={isAllMode || isLoading || updateConfig.isPending}
          data-testid="candeza-booking-trials-require-confirmation"
        />
      </ToggleRow>

      <ToggleRow>
        <ToggleInfo>
          <ToggleLabel>Allow force-override</ToggleLabel>
          <ToggleDescription>
            When on, staff can force a booking through without consuming an entitlement.
          </ToggleDescription>
        </ToggleInfo>
        <Toggle
          active={config?.flags.allow_force_override ?? true}
          onToggle={() => toggle('allow_force_override')}
          disabled={isAllMode || isLoading || updateConfig.isPending}
          data-testid="candeza-booking-allow-force-override"
        />
      </ToggleRow>
    </Card>
  );
};

export default BookingPolicySettingsSection;
